import { useMemo, useState, type ReactElement } from 'react';
import type { TimelineSpan } from '../../api/admin.js';
import { summarizeTurns, type TurnSummary } from '../../lib/session-stats.js';
import { formatDurationMs, nanoToUsdCompact } from '../../lib/format.js';

interface TurnsTableProps {
  spans: ReadonlyArray<TimelineSpan>;
  /** Turn actualmente filtrado en la waterfall, si lo hay. */
  selectedTurnId: string | null;
  onSelectTurn: (turnId: string | null) => void;
  /** Selecciona un span específico (típicamente al hacer click en un kind). */
  onSelectSpan: (spanId: string) => void;
  /** Mapa turnId → spans del turn ya filtrados; opcional, recalculado si no se pasa. */
}

function truncate(text: string | null, max: number): string {
  if (!text) return '';
  const trimmed = text.trim();
  if (trimmed.length <= max) return trimmed;
  return `${trimmed.slice(0, max - 1)}…`;
}

export function TurnsTable({
  spans,
  selectedTurnId,
  onSelectTurn,
  onSelectSpan,
}: TurnsTableProps): ReactElement {
  const turns = useMemo(() => summarizeTurns(spans), [spans]);
  const [expanded, setExpanded] = useState(true);

  if (turns.length === 0) {
    return (
      <div className="sess-turns-card" data-testid="turns-empty">
        <div className="sess-turns-head">
          <span className="sess-chart-title">Turns</span>
          <span className="muted">sin turns registrados</span>
        </div>
      </div>
    );
  }

  return (
    <div className="sess-turns-card" data-testid="turns-table">
      <div className="sess-turns-head">
        <button
          type="button"
          className="sess-turns-toggle"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          data-testid="turns-toggle"
        >
          <span aria-hidden="true">{expanded ? '▾' : '▸'}</span>
          <span className="sess-chart-title">Turns</span>
          <span className="sess-turns-count mono">({turns.length})</span>
        </button>
        {selectedTurnId !== null ? (
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => onSelectTurn(null)}
            data-testid="turns-clear-filter"
          >
            limpiar filtro
          </button>
        ) : null}
      </div>
      {expanded ? (
        <div className="sess-turns-body" role="region">
          <table className="sess-turns-tbl">
            <thead>
              <tr>
                <th className="r">#</th>
                <th>user → bot</th>
                <th className="r">stt</th>
                <th className="r">llm</th>
                <th className="r">tts</th>
                <th className="r">total</th>
                <th className="r">cost</th>
                <th className="r">⚠</th>
              </tr>
            </thead>
            <tbody>
              {turns.map((t) => (
                <TurnRow
                  key={t.turnId}
                  turn={t}
                  isSelected={selectedTurnId === t.turnId}
                  onSelectTurn={onSelectTurn}
                  onSelectSpan={onSelectSpan}
                />
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}

interface TurnRowProps {
  turn: TurnSummary;
  isSelected: boolean;
  onSelectTurn: (turnId: string | null) => void;
  onSelectSpan: (spanId: string) => void;
}

function TurnRow({ turn, isSelected, onSelectTurn, onSelectSpan }: TurnRowProps): ReactElement {
  const userSnippet = truncate(turn.userText, 60);
  const botSnippet = truncate(turn.assistantText, 60);
  return (
    <tr
      className={`sess-turn-row${isSelected ? ' is-selected' : ''}${turn.errorCount > 0 ? ' has-error' : ''}`}
      onClick={() => onSelectTurn(isSelected ? null : turn.turnId)}
      data-testid={`turn-row-${turn.turnId}`}
    >
      <td className="r mono">{turn.turnIndex}</td>
      <td className="sess-turn-snip">
        <div className="sess-turn-user" title={turn.userText ?? ''}>
          <span className="sess-turn-tag">U</span>
          {userSnippet || <span className="muted">—</span>}
        </div>
        <div className="sess-turn-bot" title={turn.assistantText ?? ''}>
          <span className="sess-turn-tag">A</span>
          {botSnippet || <span className="muted">—</span>}
        </div>
      </td>
      <td className="r mono">{turn.latency.stt > 0 ? formatDurationMs(turn.latency.stt) : '—'}</td>
      <td className="r mono">{turn.latency.llm > 0 ? formatDurationMs(turn.latency.llm) : '—'}</td>
      <td className="r mono">{turn.latency.tts > 0 ? formatDurationMs(turn.latency.tts) : '—'}</td>
      <td className="r mono">{formatDurationMs(turn.durationMs)}</td>
      <td className="r mono">{nanoToUsdCompact(turn.costNano.toString())}</td>
      <td className="r">
        {turn.errorCount > 0 ? (
          <button
            type="button"
            className="badge b-error sess-turn-err-btn"
            onClick={(e) => {
              e.stopPropagation();
              onSelectSpan(turn.turnId);
            }}
            data-testid={`turn-err-${turn.turnId}`}
          >
            {turn.errorCount}
          </button>
        ) : (
          <span className="muted">0</span>
        )}
      </td>
    </tr>
  );
}
